import React, { useState, useEffect, useRef } from 'react';
import '../styles/admin/Admin_Panel.css';
import { useNavigate, useLocation } from "react-router-dom";

const AdminPanel = () => {
    const [talkShowTitles, setTalkShowTitles] = useState([]);
    const [selectedTitle, setSelectedTitle] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const [subtopics, setSubtopics] = useState([]);
    const [showEditRow, setShowEditRow] = useState(false);
    const [editData, setEditData] = useState({ articleName: '', articleType: '', description: '' });
    const [articleTypes, setArticleTypes] = useState([]);
    const [formData, setFormData] = useState({ header_name: '', content: '' });
    const [imageFile, setImageFile] = useState(null);
    const [imageFiles, setImageFiles] = useState([null, null, null]);

    const editorRef = useRef(null);

    useEffect(() => {
        // Fetch talk shows with public-only filtering
        const fetchShows = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_BACKEND_API}/shows/`);

                if (!response.ok) {
                    throw new Error(`Failed to fetch shows: ${response.status}`);
                }

                const data = await response.json();

                // Store all shows (not just public) to get correct title
                setTalkShowTitles(data);

                // Check if showId is in the URL and set it as selected
                const params = new URLSearchParams(location.search);
                const showId = params.get('showId');
                if (showId) {
                    setSelectedTitle(showId);
                }

            } catch (err) {
                console.error('Error fetching shows:', err);
                setTalkShowTitles([]); // Fallback to empty array on error
            }
        };
        const handleAdminLogin = () => {
            localStorage.clear();
            localStorage.setItem("userId", "admin");
            localStorage.setItem("username", "AdminUser");
            navigate("/home");
        };


        // Fetch article types (unchanged)
        const fetchArticleTypes = async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_BACKEND_API}/categories/`);

                if (!res.ok) {
                    throw new Error(`Failed to fetch categories: ${res.status}`);
                }

                const data = await res.json();
                setArticleTypes(data);

            } catch (err) {
                console.error('Error fetching article types:', err);
                setArticleTypes([]); // Fallback to empty array on error
            }
        };

        // Execute both fetches
        fetchShows();
        fetchArticleTypes();

    }, [location]); // Empty dependency array means this runs once on mount

    useEffect(() => {
        const timeout = setTimeout(() => {
            alert('Session expired. Logging out.');
            handleLogout();
        }, 15 * 60 * 1000); // 15 minutes

        return () => clearTimeout(timeout); // Cleanup on unmount
    }, []);


    // Fetch subtopics when selectedTitle changes
    useEffect(() => {
        const fetchSubtopics = async () => {
            if (selectedTitle) {
                try {
                    const res = await fetch(`${process.env.REACT_APP_BACKEND_API}/subtopics/according-to-show-id/${selectedTitle}/`);
                    const data = await res.json();
                    // Filter out deleted subtopics if needed
                    const activeSubtopics = data.filter(sub =>
                        !sub.is_deleted && !sub.deleted && !sub.isDeleted
                    );
                    setSubtopics(activeSubtopics);
                } catch (err) {
                    console.error('Error fetching subtopics:', err);
                }
            } else {
                setSubtopics([]);
            }
        };

        fetchSubtopics();
    }, [selectedTitle]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleMultipleImageChange = (e, index) => {
        const newImages = [...imageFiles];
        newImages[index] = e.target.files[0];
        setImageFiles(newImages);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.header_name || !formData.content) {
            alert('Please fill in all fields before submitting.');
            return;
        }

        const form = new FormData();
        form.append('show', selectedTitle);
        form.append('header_name', formData.header_name);
        form.append('content', formData.content);

        imageFiles.forEach((file) => {
            if (file) form.append('images', file);
        });

        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_API}/talkshow-web/upload-subtopic/`, {
                method: 'POST',
                body: form,
            });
            const result = await res.json();

            if (!res.ok) {
                throw new Error(result?.detail || 'Upload failed');
            }

            // Refresh subtopics after upload
            const refreshRes = await fetch(`${process.env.REACT_APP_BACKEND_API}/subtopics/according-to-show-id/${selectedTitle}/`);
            const data = await refreshRes.json();
            setSubtopics(data);

            alert('Subtopic uploaded successfully!');
            setFormData({ header_name: '', content: '' });
            setImageFile(null);
            setImageFiles([null, null, null]);
            if (editorRef.current) {
                editorRef.current.innerHTML = '';
            }
        } catch (error) {
            console.error('Upload error:', error);
            alert('Failed to upload subtopic.');
        }
    };

    const execCommand = (cmd) => {
        document.execCommand(cmd, false, null);
    };

    const handleEditChange = (e) => {
        setEditData({ ...editData, [e.target.name]: e.target.value });
    };

    const handleUpdate = async () => {
        // Debug: Log current state
        console.log('Attempting update with:', {
            selectedTitle,
            editData
        });

        if (!selectedTitle) {
            alert('Please select a talk show first.');
            return;
        }

        if (!editData.articleName || !editData.articleType) {
            alert('Please fill in Article Name, Type and Description.');
            return;
        }

        try {
            // Convert selectedTitle to number if backend expects numeric ID
            const showId = Number(selectedTitle);

            // Find category ID from the name
            const selectedCategory = articleTypes.find(t => t.name === editData.articleType);
            
            // Debug: Verify the ID
            console.log('Using show ID:', showId);
            console.log('Using category ID:', selectedCategory?.id);

            const payload = {
                title: editData.articleName,
                description: editData.description,
            };

            if (selectedCategory) {
                payload.category = selectedCategory.id;
            }

            // Use the standard shows endpoint with PATCH
            const response = await fetch(`${process.env.REACT_APP_BACKEND_API}/shows/${showId}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    // Add authentication header if required
                    // 'Authorization': `Bearer ${yourToken}`
                },
                body: JSON.stringify(payload),
            });

            // Debug: Log raw response
            console.log('Raw response:', response);

            const result = await response.json();

            // Debug: Log parsed response
            console.log('Response data:', result);

            if (!response.ok) {
                throw new Error(result.detail || result.message || 'Update failed');
            }

            alert(`✅ ${result.message || 'Show updated successfully!'}`);

            // Dispatch event to update Navbar
            window.dispatchEvent(new Event('topicsUpdated'));

            // Refresh data
            const refreshResponse = await fetch(`${process.env.REACT_APP_BACKEND_API}/shows/`);
            const updatedShows = await refreshResponse.json();
            setTalkShowTitles(updatedShows);

            // Reset UI state
            setShowEditRow(false);
            setEditData({ articleName: '', articleType: '', description: '' });
            setSelectedTitle('');

        } catch (error) {
            console.error('Update failed:', {
                error: error.message,
                stack: error.stack
            });
            alert(`❌ Update failed: ${error.message}`);
        }
    };

    const handleLogout = () => {
        // Add your logout logic here
        const confirmLogout = window.confirm('Are you sure you want to logout?');
        if (confirmLogout) {
            // Clear any stored authentication tokens
            localStorage.removeItem('authToken');
            sessionStorage.removeItem('authToken');

            // Redirect to login page or home page
            window.location.href = '/login'; // Adjust the path as needed
            navigate("/login");
            // Or if using React Router:
            // navigate('/login');
        }
    };

    const handleAdminLogin = () => {
        localStorage.clear();
        localStorage.setItem("userId", "admin");
        localStorage.setItem("username", "AdminUser");
        navigate("/home");
    };
    // Get the selected talk show title for display
    const selectedShow = talkShowTitles.find(show => show.id.toString() === selectedTitle);
    const selectedShowTitle = selectedShow ? selectedShow.title : selectedTitle;

    const [activeCommands, setActiveCommands] = useState([]);
    const [activeAlignment, setActiveAlignment] = useState('');


    const toggleCommand = (command) => {
        if (["justifyLeft", "justifyCenter", "justifyRight", "justifyFull"].includes(command)) {
            // If already active, toggle off
            setActiveAlignment(prev => prev === command ? '' : command);
            document.execCommand(command, false, null);
        } else {
            // bold/italic etc
            document.execCommand(command, false, null);
            const isActive = document.queryCommandState(command);
            setActiveCommands(prev => {
                if (isActive) {
                    if (!prev.includes(command)) return [...prev, command];
                    return prev;
                } else {
                    return prev.filter(c => c !== command);
                }
            });
        }
    };



    return (
        <div className="admin-container">
            <div className="top-bar">
                {/* <button className="logout-button1" onClick={handleLogout}>
            Logout
        </button>
        <button className="admin-button" onClick={handleAdminLogin}>
        AdminUser
    </button> */}
            </div>

            <div className="dropdown-row">
                
                <div className="dropdown-section">
                    <label>Select Talk Show:</label>
                    <select
                        value={selectedTitle}
                        onChange={(e) => setSelectedTitle(e.target.value)}
                    >
                        <option value="">-- Choose a Talk Show --</option>
                        {talkShowTitles.map((show) => (
                            <option key={show.id} value={show.id}>{show.title}</option>
                        ))}
                    </select>
                    {selectedTitle && (
                        <button
                            className="change-button"
                            onClick={() => {
                                setShowEditRow(!showEditRow);
                                // Pre-fill the edit form with current values
                                if (selectedShow) {
                                    setEditData({
                                        articleName: selectedShow.title,
                                        articleType: selectedShow.category_name || '',
                                        description: selectedShow.description || ''
                                    });
                                }
                            }}
                        >
                            Edit
                        </button>
                    )}
                </div>
               
            </div>

            {showEditRow && (
                <div className="edit-row" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', gap: '10px', width: '100%', marginBottom: '10px' }}>
                        <input
                            type="text"
                            name="articleName"
                            placeholder="Article Name"
                            value={editData.articleName}
                            onChange={handleEditChange}
                            style={{ flex: 1 }}
                        />

                        <select
                            name="articleType"
                            value={editData.articleType}
                            onChange={handleEditChange}
                            style={{ flex: 1 }}
                        >
                            <option value="">-- Select Article Type --</option>
                            {articleTypes.map((type) => (
                                <option key={type.id} value={type.name}>
                                    {type.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <textarea
                        name="description"
                        placeholder="Description"
                        value={editData.description}
                        onChange={handleEditChange}
                        style={{ width: '100%', minHeight: '100px', marginBottom: '10px', padding: '8px' }}
                    />

                    <button className="update-button" onClick={handleUpdate}>
                        Update
                    </button>
                </div>
            )}

            {/* Rest of your component remains the same */}
            {selectedTitle && (
                <div className="article-form-container">
                    <h2>Add Sub Topics for: <span style={{ color: '#0D6EFD' }}>{selectedShowTitle}</span></h2>
                    <form onSubmit={handleSubmit} className="article-form">
                        <label>Sub Topic Header</label>
                        <input
                            className="sub-topic-header"
                            type="text"
                            name="header_name"
                            value={formData.header_name}
                            onChange={handleChange}
                            required
                        />

                        <label>Content</label>
                        <div className="toolbar">
                            <button className={`toolbar-button ${activeCommands.includes("bold") ? "active" : ""}`} type="button" onClick={() => toggleCommand('bold')}>Bold</button>
                            <button className={`toolbar-button ${activeCommands.includes("italic") ? "active" : ""}`} type="button" onClick={() => toggleCommand('italic')}>Italic</button>
                            <button className={`toolbar-button ${activeAlignment === "justifyLeft" ? "active" : ""}`} type="button" onClick={() => toggleCommand('justifyLeft')}>Left</button>
                            <button className={`toolbar-button ${activeAlignment === "justifyCenter" ? "active" : ""}`} type="button" onClick={() => toggleCommand('justifyCenter')}>Center</button>
                            <button className={`toolbar-button ${activeAlignment === "justifyRight" ? "active" : ""}`} type="button" onClick={() => toggleCommand('justifyRight')}>Right</button>
                            <button className={`toolbar-button ${activeAlignment === "justifyFull" ? "active" : ""}`} type="button" onClick={() => toggleCommand('justifyFull')}>Justify</button>
                        </div>


                        <div
                            className={`custom-editor ${formData.content ? "has-value" : ""}`}
                            contentEditable
                            ref={editorRef}
                            onInput={(e) => setFormData({ ...formData, content: e.currentTarget.innerHTML })}
                        ></div>

                        <label>Images (up to 3)</label>
                        <div className="image-upload-rows">
                            {[0, 1, 2].map((index) => (
                                <input
                                    key={index}
                                    className="image-upload"
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleMultipleImageChange(e, index)}
                                />
                            ))}
                        </div>

                        <button className="subtopic-submit-btn" type="submit">Add Subtopic</button>
                    </form>
                </div>
            )}

            {subtopics.length > 0 && (
                <div className="subtopics-list">
                    <h3>Added Subtopics for {selectedShowTitle}</h3>
                    <div className="subtopic-cards">
                        {subtopics.map((sub, index) => (
                            <div key={index} className="subtopic-card1">
                                <h4>{sub.header_name}</h4>
                                <div
                                    className="subtopic-card-content"
                                    dangerouslySetInnerHTML={{ __html: sub.content }}
                                />
                                {sub.image_urls && sub.image_urls.length > 0 && (
                                    <div className="subtopic-images">
                                        {sub.image_urls.map((url, imgIndex) => (
                                            <img
                                                key={imgIndex}
                                                src={url}
                                                alt={`Subtopic ${index + 1}`}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPanel;